import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { auth } from './firebase';

export interface ApiResponse<T = unknown> {
  data: T;
  message: string;
  success?: boolean;
  status?: string;
  requestId?: string;
}

export interface ApiErrorResponse {
  success: false;
  message: string;
  requestId?: string;
  error?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly data?: ApiErrorResponse;
  readonly requestId?: string;

  constructor(message: string, status: number, data?: ApiErrorResponse) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.requestId = data?.requestId;
  }
}

class ApiClient {
  private instance: AxiosInstance;

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL || '') {
    this.instance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store',
        Pragma: 'no-cache',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(
      async config => {
        try {
          if (auth?.currentUser) {
            const token = await auth.currentUser.getIdToken();
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn('Failed to get auth token:', error);
        }
        return config;
      },
      error => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: unknown) => {
        if (axios.isAxiosError<unknown>(error)) {
          const responseData = error.response?.data;
          const apiErrorData =
            typeof responseData === 'object' &&
            responseData !== null &&
            'success' in responseData &&
            responseData.success === false &&
            'message' in responseData &&
            typeof responseData.message === 'string'
              ? (responseData as ApiErrorResponse)
              : undefined;

          return Promise.reject(
            new ApiError(
              apiErrorData?.message || error.message || 'Network error',
              error.response?.status ?? 0,
              apiErrorData
            )
          );
        }

        return Promise.reject(
          new ApiError(
            error instanceof Error ? error.message : 'Network error',
            0
          )
        );
      }
    );
  }

  async get<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.post<ApiResponse<T>>(
      url,
      data,
      config
    );
    return response.data;
  }

  async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.instance.patch<ApiResponse<T>>(
      url,
      data,
      config
    );
    return response.data;
  }
}

const apiClient = new ApiClient();

export default apiClient;
